// src/pages/Contexts/AuthContext.tsx
// Customer authentication for the client website (Sign In / Sign Up / Forgot
// Password). Mirrors admin/src/contexts/AuthContext.tsx but backs onto the
// `customers` collection instead of `userClaims` — see Services/customerService.ts.
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../Firebase/Config';
import { createCustomerProfile, getCustomerProfile, CustomerProfile } from '../Services/customerService';

interface AuthContextType {
  currentUser: User | null;
  customerProfile: CustomerProfile | null;
  loading: boolean;
  signup: (fullName: string, email: string, password: string, phone?: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (uid: string) => {
    try {
      const profile = await getCustomerProfile(uid);
      setCustomerProfile(profile);
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      setCustomerProfile(null);
    }
  };

  const signup = async (fullName: string, email: string, password: string, phone?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    try {
      if (fullName) {
        await updateProfile(userCredential.user, { displayName: fullName });
      }
      await createCustomerProfile(userCredential.user.uid, fullName, email, phone);
      await fetchProfile(userCredential.user.uid);
    } catch (error) {
      console.error('Error creating customer profile:', error);
    }
    return userCredential;
  };

  // Blocks sign-in for accounts an admin has deactivated.
  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    if (userCredential.user?.uid) {
      const profile = await getCustomerProfile(userCredential.user.uid);

      if (profile && !profile.isActive) {
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact us for help.');
      }

      setCustomerProfile(profile);
    }

    return userCredential;
  };

  const logout = async () => {
    await signOut(auth);
    setCustomerProfile(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Re-reads the Firestore customer doc — call after updating the profile
  // (e.g. new avatar, edited name) so the UI reflects the change immediately.
  const refreshProfile = async () => {
    if (currentUser?.uid) {
      await fetchProfile(currentUser.uid);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user?.uid) {
        await fetchProfile(user.uid);
      } else {
        setCustomerProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    customerProfile,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
